class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  connectedCallback() {
    this.bindDesktopDisclosure();
  }

  disconnectedCallback() {
    this.disclosureCleanup?.();
    this.disclosureCleanup = null;
    this.syncDesktopDisclosure = null;
  }
  bindDesktopDisclosure() {
    if (!this.closest('.header__inline-menu') || this.disclosureCleanup) return;
    const shell = this;
    const details = this.mainDetailsToggle;
    const summary = details?.querySelector(':scope > summary');
    const content = summary?.nextElementSibling;
    if (!shell || !summary || !content) return;
    const controller = new AbortController();
    const { signal } = controller;
    const hover = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    let closeTimer;
    let keyboardInteraction = false;
    const cancelClose = () => { clearTimeout(closeTimer); closeTimer = null; };
    const inside = target => target instanceof Node && (summary.contains(target) || content.contains(target));
    const sync = () => {
      summary.setAttribute('aria-expanded', String(details.open));
      const header = shell.header || shell.closest('.header-wrapper');
      if (header) header.preventHide = [...header.querySelectorAll('header-menu > details')].some(item => item.open);
      if (!details.open) cancelClose();
    };
    const close = () => { cancelClose(); details.open = false; sync(); };
    const enter = event => {
      if (!hover.matches || event.pointerType !== 'mouse') return;
      cancelClose();
      if (!details.open) {
        keyboardInteraction = false;
        shell.closest('nav')?.querySelectorAll('header-menu').forEach(other => {
          if (other !== shell) other.close?.();
        });
        details.open = true;
        sync();
      }
    };
    const leave = event => {
      if (!hover.matches || event.pointerType !== 'mouse' || inside(event.relatedTarget)) return;
      cancelClose();
      closeTimer = setTimeout(() => {
        closeTimer = null;
        // Mouse departure must not hide content currently being used from the keyboard.
        if (keyboardInteraction && details.contains(document.activeElement)) return;
        close();
      }, 180);
    };
    [summary, content].forEach(element => {
      element.addEventListener('pointerenter', enter, { signal });
      element.addEventListener('pointerleave', leave, { signal });
    });
    details.addEventListener('keydown', () => { keyboardInteraction = true; cancelClose(); }, { signal });
    details.addEventListener('pointerdown', () => { keyboardInteraction = false; }, { signal });
    details.addEventListener('toggle', sync, { signal });
    document.addEventListener('pointerdown', event => {
      if (hover.matches && details.open && !inside(event.target)) close();
    }, { signal });
    hover.addEventListener('change', () => { if (!hover.matches) close(); }, { signal });
    this.disclosureCleanup = () => { controller.abort(); close(); };
    this.syncDesktopDisclosure = sync;
    sync();
  }

  close() {
    super.close();
    this.syncDesktopDisclosure?.();
  }

  onToggle() {
    if (!this.header) return;
    this.syncDesktopDisclosure?.();
    this.header.preventHide = [...this.header.querySelectorAll('header-menu > details')].some(details => details.open);

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
